import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, ROLES, EMPLOYEE_STATUSES } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Envelope, Prohibit, PauseCircle, ArrowClockwise, MaskHappy, StackSimple, X, Key, TestTube, Trash, Fire, Broom, Archive } from "@phosphor-icons/react";
import { RemoveEmployeeDialog } from "@/components/employees/RemoveEmployeeDialog";
import { HandoverNote } from "@/components/employees/HandoverNote";
import CleanStartDialog from "@/components/cleanStart/CleanStartDialog";
import CleanStartBackups from "@/components/cleanStart/CleanStartBackups";
import { toast } from "sonner";
import SetPasswordDialog from "@/components/SetPasswordDialog";
import SeedTestAccountsDialog from "@/components/SeedTestAccountsDialog";
import FactoryResetDialog from "@/components/FactoryResetDialog";
import FactoryResetSnapshotsPanel from "@/components/FactoryResetSnapshotsPanel";
import { NewUserWizardButton } from "@/components/user-wizard/NewUserWizardButton";
import { ResendWelcomeButton, ResendFromLink, needsWelcome } from "@/components/user-wizard/ResendWelcome";
import { FixEmailButton, canFixEmail } from "@/components/user-wizard/FixEmail";
import { OnboardingProgress } from "@/components/user-wizard/OnboardingProgress";
import { OnboardingFilter } from "@/components/user-wizard/OnboardingFilter";
import { AllPagesToggle } from "@/components/traffic/AllPagesToggle";

/**
 * Iter-65: Assign landing pages to a media-buyer employee.
 *
 * Fetches every non-deleted custom landing page, shows a checklist, and
 * PATCHes `assigned_landing_page_ids` back to /api/employees/{id}.
 * Admin-only surface — hidden entirely on non-media-buyer rows.
 *
 * Empty selection = "sees no pages" (strict default per Iter-65). The
 * dialog surfaces this explicitly so the operator understands the
 * consequence of saving with zero pages checked.
 */
function AssignPagesDialog({ employee, onClose, onSaved }) {
  const [pages, setPages] = useState(null);
  const [selected, setSelected] = useState(new Set(employee.assigned_landing_page_ids || []));
  const [allPages, setAllPages] = useState(Boolean(employee.traffic_all_pages));
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/admin/custom-landing-pages"), api.get("/admin/global-funnels")])
      .then(([c, g]) => {
        const shared = (g.data.funnels || []).map((f) => ({ ...f, firm_name: `Shared page: ${f.title}`, shared: true }));
        setPages([...shared, ...(c.data.pages || []).map((p) => ({ ...p, own: Boolean(p.buyer_lander_of) }))]);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load landing pages"));
  }, []);

  const filtered = useMemo(() => {
    if (!pages) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return pages;
    return pages.filter((p) => {
      const hay = `${p.slug || ""} ${p.firm_name || ""} ${p.buyer_destination_name || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [pages, q]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/employees/${employee.id}`, {
        assigned_landing_page_ids: Array.from(selected),
      });
      toast.success(selected.size === 0
        ? `${employee.name} now sees NO landing pages`
        : `Assigned ${selected.size} page${selected.size === 1 ? "" : "s"} to ${employee.name}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="assign-pages-dialog">
      <div className="bg-white border border-black brut-shadow-sm w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-black px-4 py-3">
          <div>
            <div className="label-mono">Landing page assignment</div>
            <div className="font-chivo font-black text-xl uppercase tracking-tighter">{employee.name}</div>
            <div className="text-[11px] font-mono text-zinc-600">{employee.email}</div>
          </div>
          <button onClick={onClose} data-testid="assign-pages-close" className="border border-black p-1 hover:bg-black hover:text-white">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-black">
          <div className="mb-2">
            <AllPagesToggle kind="media_buyer" id={employee.id} value={allPages} testId="assign-pages-all-pages"
                            onChange={(d) => { setAllPages(d.all_pages); setSelected(new Set(d.page_ids)); onSaved(); }} />
          </div>
          <input
            data-testid="assign-pages-search"
            placeholder="Search by slug, firm name, or destination"
            className="border border-black px-3 py-2 w-full text-sm"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="mt-2 flex items-center justify-between text-[11px] font-mono">
            <span>{selected.size} of {pages?.length || 0} selected</span>
            <span className={selected.size === 0 ? "text-red-700 font-bold" : "text-zinc-600"}>
              {selected.size === 0 ? "Empty = sees NO landing pages" : "Only these links show for this media buyer, and only these earn them credit"}
            </span>
          </div>
          {allPages && (
            <div className="mt-2 text-[11px] text-emerald-800" data-testid="assign-pages-all-note">
              All pages is on, so every live page is included. Turn it off to pick pages one by one.
            </div>
          )}
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-zinc-200">
          {pages === null && <div className="p-6 text-center label-mono">Loading…</div>}
          {pages !== null && filtered.length === 0 && (
            <div className="p-6 text-center label-mono">No pages match your search</div>
          )}
          {filtered.map((p) => {
            const checked = selected.has(p.id);
            return (
              <label
                key={p.id}
                data-testid={`assign-page-row-${p.id}`}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-50 ${checked ? "bg-yellow-50" : ""}`}
              >
                <input
                  type="checkbox"
                  data-testid={`assign-page-check-${p.id}`}
                  checked={checked}
                  disabled={allPages}
                  onChange={() => toggle(p.id)}
                  className="w-4 h-4"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">
                    /lp/{p.slug} <span className="text-zinc-500">— {p.firm_name || "—"}</span>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                    {p.status || "draft"} · {p.shared ? "goes to whichever buyer matches" : p.own ? "buyer's own page" : (p.buyer_destination_name || "no destination")}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="border-t border-black px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <button
              onClick={() => setSelected(new Set())}
              data-testid="assign-pages-clear"
              className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Clear all
            </button>
            <button
              onClick={() => setSelected((s) => new Set([...s, ...(pages || []).filter((p) => p.shared).map((p) => p.id)]))}
              data-testid="assign-pages-all-shared"
              className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Tick all shared pages
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="assign-pages-save"
              className="border border-black bg-black text-white px-3 py-1.5 text-[11px] uppercase tracking-widest hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save assignment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Employees() {
  const { user, hasPerm, refresh } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [onboarding, setOnboarding] = useState("");
  const [onboardingCounts, setOnboardingCounts] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [assignTarget, setAssignTarget] = useState(null);
  // Iter-69 — target of the "Set password" dialog. Only super_admin
  // / internal admins with `manage_employees` see the button; the
  // backend re-enforces the same guard.
  const [pwTarget, setPwTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  // Iter-70 — controls the "Seed test accounts" dialog. Only super_admin
  // sees the button; the backend enforces the same guard.
  const [seedOpen, setSeedOpen] = useState(false);
  // Iter-71: state for the "Purge test accounts" confirmation flow.
  const [purging, setPurging] = useState(false);
  // Iter-73: factory-reset dialog (super_admin only — nukes tenant data).
  const [factoryOpen, setFactoryOpen] = useState(false);
  const [cleanOpen, setCleanOpen] = useState(false);
  const [cleanKey, setCleanKey] = useState(0);
  // Iter-73.1: bump to force the snapshots panel to reload after a wipe.
  const [snapshotsRefreshKey, setSnapshotsRefreshKey] = useState(0);
  const canImpersonate = hasPerm("impersonate_users") && user?.org_type === "internal";
  const canAssignPages = hasPerm("manage_employees") && user?.org_type === "internal";
  const canSetPassword = hasPerm("manage_employees");
  const canSeedTests = user?.role === "super_admin";

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const loadSeq = useRef(0);
  const load = async () => {
    const seq = ++loadSeq.current;
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status) p.set("status", status);
    if (onboarding) p.set("onboarding", onboarding);
    const { data } = await api.get(`/employees?${p}`);
    if (seq !== loadSeq.current) return;
    setRows(data.employees);
    setOnboardingCounts(data.onboarding_counts);
    setSelected({});
  };

  useEffect(() => {
    // Auto-refetch when the status filter changes. Search input goes through
    // the Apply button (see load() call). Intentionally not watching `q`.
    load();
  }, [status, onboarding]);

  const action = async (id, patch, label) => {
    try {
      await api.patch(`/employees/${id}`, patch);
      toast.success(label);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/employees/${id}/resend-invitation`);
      navigator.clipboard?.writeText(data.invitation_url).catch(() => {});
      if (data.email_sent) {
        toast.success("Invitation resent");
      } else {
        toast.warning("The invitation email couldn't be sent. The link is copied so you can share it.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/employees/${id}/cancel-invitation`);
      toast.success("Invitation cancelled");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter-71: one-click purge of every seeded canary user. Two-step
  // `confirm()` because this crosses org boundaries (deletes buyer +
  // vendor test accounts in other orgs) — belt-and-braces before we
  // fire the DELETE.
  const purgeTestAccounts = async () => {
    const testAccountCount = rows.filter((r) => r.is_test_account).length;
    if (testAccountCount === 0) {
      return toast.info("No test accounts on this page — nothing to purge.");
    }
    if (!confirm(
      `Permanently DELETE every user with is_test_account=true?\n\n`
      + `This will remove seeded QA canaries across ALL orgs `
      + `(employee, media_buyer, buyer, affiliate) in one shot. `
      + `Real users are never touched.\n\n`
      + `Currently visible on this page: ${testAccountCount} test account(s).`,
    )) return;
    setPurging(true);
    try {
      const { data } = await api.delete("/employees/test-accounts");
      toast.success(
        data.deleted > 0
          ? `Purged ${data.deleted} test account${data.deleted === 1 ? "" : "s"}`
          : "No test accounts to purge",
      );
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); }
    finally { setPurging(false); }
  };

  const impersonate = async (id, email) => {
    if (!confirm(`Impersonate ${email}? Every action will be logged.`)) return;
    try {
      await api.post(`/auth/impersonate/${id}`);
      toast.success(`Now viewing as ${email}`);
      await refresh();
      nav("/", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bulk = async (bulkAction) => {
    if (!selectedIds.length) return toast.error("Select at least one employee");
    if (!confirm(`Apply "${bulkAction}" to ${selectedIds.length} users?`)) return;
    try {
      const { data } = await api.post("/bulk/employees", {
        action: bulkAction, user_ids: selectedIds,
      });
      toast.success(`Updated ${data.updated}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleAll = (e) => {
    const next = {};
    if (e.target.checked) rows.forEach((r) => { if (r.id !== user?.id) next[r.id] = true; });
    setSelected(next);
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="label-mono">People</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Employees</h1>
        </div>
        {hasPerm("manage_employees") && (
          <div className="flex flex-wrap items-center gap-2">
            {canSeedTests && (
              <button
                onClick={() => setSeedOpen(true)}
                data-testid="seed-test-accounts-btn"
                className="border border-black bg-amber-300 px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100"
                title="Create (or password-rotate) 4 canary users — one per role"
              >
                <TestTube size={14} weight="bold" /> Seed test accounts
              </button>
            )}
            {canSeedTests && (
              <button
                onClick={purgeTestAccounts}
                disabled={purging}
                data-testid="purge-test-accounts-btn"
                className="border border-red-700 bg-white text-red-700 px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-red-700 hover:text-white transition-colors duration-100 disabled:opacity-40"
                title="Permanently delete every user with is_test_account=true"
              >
                <Trash size={14} weight="bold" /> {purging ? "Purging…" : "Purge test accounts"}
              </button>
            )}
            {canSeedTests && (
              <button
                onClick={() => setFactoryOpen(true)}
                data-testid="factory-reset-btn"
                className="border border-rose-900 bg-rose-100 text-rose-900 px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-rose-900 hover:text-white transition-colors duration-100"
                title="Wipe every tenant-scoped row (clients, affiliates, leads, campaigns, destinations, buyer/affiliate users, test canaries). Preserves every real employee + internal org + platform config. 24 h undo snapshot writes automatically."
              >
                <Fire size={14} weight="fill" /> Factory reset
              </button>
            )}
            {canSeedTests && (
              <button
                onClick={() => setCleanOpen(true)}
                data-testid="clean-start-btn"
                className="border border-amber-900 bg-amber-100 text-amber-950 px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-amber-900 hover:text-white transition-colors duration-100"
                title="Go-live clean start: removes buyers, media buyers, vendors, affiliates, leads and past activity. Keeps staff, test accounts, company pages and settings. 24 h undo."
              >
                <Broom size={14} weight="fill" /> Clean start for go-live
              </button>
            )}
            <NewUserWizardButton testId="employees-new-user-wizard-btn" />
            <Link to="/employees/invite" data-testid="invite-employee-btn"
              className="bg-black text-white px-3 py-2 text-xs uppercase tracking-widest border border-black inline-flex items-center gap-2 hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
              <UserPlus size={14} weight="bold" /> Invite / Create
            </Link>
          </div>
        )}
      </div>

      <div className="brut-card p-4 flex flex-wrap items-center gap-3">
        <input
          data-testid="emp-search"
          placeholder="Search name or email"
          className="border border-black px-3 py-2 flex-1 min-w-[220px]"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
        />
        <select data-testid="emp-status-filter" className="border border-black px-2 py-2"
          value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          {EMPLOYEE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load}
          className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-100">
          Apply
        </button>
        <div className="basis-full">
          <OnboardingFilter value={onboarding} onChange={setOnboarding} counts={onboardingCounts} />
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="mt-3 border border-black bg-yellow-100 p-3 flex items-center justify-between" data-testid="bulk-employee-bar">
          <div className="text-sm font-mono">
            <span className="font-bold">{selectedIds.length}</span> selected
          </div>
          <div className="flex gap-2">
            <button onClick={() => bulk("suspend")} data-testid="bulk-suspend"
              className="border border-black bg-white px-3 py-1 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">Suspend</button>
            <button onClick={() => bulk("activate")} data-testid="bulk-activate"
              className="border border-black bg-white px-3 py-1 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">Activate</button>
            <button onClick={() => bulk("deactivate")} data-testid="bulk-deactivate"
              className="border border-red-600 text-red-700 bg-white px-3 py-1 text-[11px] uppercase tracking-widest hover:bg-red-600 hover:text-white">Deactivate</button>
          </div>
        </div>
      )}

      <div className="brut-card mt-4">
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[1040px]" data-testid="emp-table">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-2 text-left">
                <input type="checkbox" data-testid="emp-select-all"
                  onChange={toggleAll}
                  checked={selectedIds.length > 0 && selectedIds.length === rows.filter((r) => r.id !== user?.id).length}
                />
              </th>
              <th className="py-2 px-3 text-left">Name</th>
              <th className="py-2 px-3 text-left">Email</th>
              <th className="py-2 px-3 text-left">Role</th>
              <th className="py-2 px-3 text-left">Org</th>
              <th className="py-2 px-3 text-left">Status</th>
              <th className="py-2 px-3 text-left">Onboarding</th>
              <th className="py-2 px-3 text-left">Last login</th>
              <th className="py-2 px-3 text-left">Created</th>
              <th className="py-2 px-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-zinc-200 hover:bg-zinc-100" data-testid={`emp-row-${u.id}`}>
                <td className="py-2 px-2">
                  {u.id !== user?.id && (
                    <input type="checkbox" data-testid={`emp-select-${u.id}`}
                      checked={!!selected[u.id]}
                      onChange={(e) => setSelected({ ...selected, [u.id]: e.target.checked })} />
                  )}
                </td>
                <td className="py-2 px-3">
                  {u.name}
                  {u.is_test_account && (
                    <span
                      data-testid={`emp-test-chip-${u.id}`}
                      className="ml-2 inline-block bg-amber-200 border border-amber-700 text-amber-900 px-1.5 py-0.5 text-[9px] uppercase tracking-widest align-middle"
                      title="Seeded test account — is_test_account=true"
                    >
                      TEST
                    </span>
                  )}
                  <HandoverNote user={u} testId={`emp-handover-${u.id}`} />
                </td>
                <td className="py-2 px-3">{u.email}</td>
                <td className="py-2 px-3">{ROLES[u.role] || u.role}</td>
                <td className="py-2 px-3">{u.org_type}</td>
                <td className="py-2 px-3"><StatusBadge status={u.status} testId={`emp-status-${u.id}`} /></td>
                <td className="py-2 px-3"><OnboardingProgress user={u} testId={`emp-onboarding-${u.id}`} /></td>
                <td className="py-2 px-3 text-[11px]">{u.last_login_at ? fmtDate(u.last_login_at) : "—"}</td>
                <td className="py-2 px-3 text-[11px]">{fmtDate(u.created_at)}</td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap gap-1">
                    {canImpersonate && u.id !== user?.id && u.status === "active" && (
                      <button onClick={() => impersonate(u.id, u.email)} data-testid={`emp-impersonate-${u.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                        <MaskHappy size={10} className="inline" /> Impersonate
                      </button>
                    )}
                    {/* Iter-65: assign landing pages to media buyers */}
                    {canAssignPages && u.role === "media_buyer" && u.status !== "archived" && (
                      <button onClick={() => setAssignTarget(u)} data-testid={`emp-assign-pages-${u.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                        <StackSimple size={10} className="inline" /> Pages ({u.traffic_all_pages ? "all" : (u.assigned_landing_page_ids || []).length})
                      </button>
                    )}
                    {/* Iter-69: admin-initiated password reset. Hidden for self —
                        the backend also refuses self-target on this route. */}
                    {canSetPassword && u.id !== user?.id && !["invitation_pending", "invitation_expired", "archived"].includes(u.status) && (
                      <button onClick={() => setPwTarget(u)} data-testid={`emp-set-password-${u.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                        <Key size={10} className="inline" /> Password
                      </button>
                    )}
                    {["invited", "invitation_pending", "invitation_expired"].includes(u.status) && (
                      <>
                        <button onClick={() => resend(u.id)} title="Resend"
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white" data-testid={`emp-resend-${u.id}`}>
                          <Envelope size={10} className="inline" /> Resend
                        </button>
                        <button onClick={() => cancel(u.id)} title="Cancel"
                          className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white" data-testid={`emp-cancel-${u.id}`}>
                          <Prohibit size={10} className="inline" /> Cancel
                        </button>
                      </>
                    )}
                    {needsWelcome(u) && (
                      <ResendWelcomeButton user={u} onDone={load} testId={`emp-resend-welcome-${u.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-50" />
                    )}
                    {canFixEmail(u) && u.id !== user?.id && (
                      <FixEmailButton user={u} onDone={load} testId={`emp-fix-email-${u.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white" />
                    )}
                    {u.status === "active" && u.id !== user?.id && (
                      <>
                        <button onClick={() => action(u.id, { status: "suspended" }, "Suspended")} data-testid={`emp-suspend-${u.id}`}
                          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                          <PauseCircle size={10} className="inline" /> Suspend
                        </button>
                        <button onClick={() => action(u.id, { status: "deactivated" }, "Deactivated")} data-testid={`emp-deactivate-${u.id}`}
                          className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                          Deactivate
                        </button>
                      </>
                    )}
                    {["suspended", "deactivated", "locked"].includes(u.status) && (
                      <button onClick={() => action(u.id, { status: "active" }, "Reactivated")} data-testid={`emp-activate-${u.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                        <ArrowClockwise size={10} className="inline" /> Reactivate
                      </button>
                    )}
                    {canSetPassword && u.id !== user?.id && u.status !== "archived" && (
                      <button onClick={() => setRemoveTarget(u)} data-testid={`emp-remove-${u.id}`}
                        className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                        <Archive size={10} className="inline" /> Remove
                      </button>
                    )}
                    {u.status === "archived" && (
                      <span className="text-[10px] text-zinc-500" data-testid={`emp-archived-note-${u.id}`}>
                        Was {u.archived_email}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={10} className="py-6 text-center label-mono">{onboarding ? "Everyone here has finished onboarding" : "No employees"}</td></tr>}
          </tbody>
        </table>
        </div>
        <ResendFromLink rows={rows} onDone={load} />
        {/* Mobile cards */}
        <div className="md:hidden p-2 flex flex-col gap-2" data-testid="emp-cards">
          {rows.map((u) => (
            <div key={u.id} className="border border-black bg-white p-3" data-testid={`emp-card-${u.id}`}>
              <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                <div className="font-bold text-sm truncate flex-1">{u.name}</div>
                <StatusBadge status={u.status} testId={`emp-status-mobile-${u.id}`} />
              </div>
              <div className="text-[11px] font-mono text-zinc-600 truncate">{u.email}</div>
              <HandoverNote user={u} testId={`emp-handover-mobile-${u.id}`} />
              <div className="flex items-center gap-3 mt-1 text-xs">
                <span className="uppercase tracking-widest text-[10px] bg-black text-white px-1.5 py-0.5">{ROLES[u.role] || u.role}</span>
                <span className="label-mono">{u.org_type}</span>
              </div>
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Last login {u.last_login_at ? fmtDate(u.last_login_at) : "—"}
              </div>
              <OnboardingProgress user={u} testId={`emp-onboarding-mobile-${u.id}`} className="mt-2 flex flex-wrap gap-x-3 gap-y-1" />
              <div className="mt-2 flex flex-wrap gap-1">
                {canImpersonate && u.id !== user?.id && u.status === "active" && (
                  <button onClick={() => impersonate(u.id, u.email)} data-testid={`emp-impersonate-mobile-${u.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                    <MaskHappy size={10} className="inline" /> Impersonate
                  </button>
                )}
                {/* Iter-65: mobile assign-pages entry for media buyers */}
                {canAssignPages && u.role === "media_buyer" && u.status !== "archived" && (
                  <button onClick={() => setAssignTarget(u)} data-testid={`emp-assign-pages-mobile-${u.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                    <StackSimple size={10} className="inline" /> Pages ({u.traffic_all_pages ? "all" : (u.assigned_landing_page_ids || []).length})
                  </button>
                )}
                {/* Iter-69: mobile admin-initiated password reset. */}
                {canSetPassword && u.id !== user?.id && !["invitation_pending", "invitation_expired", "archived"].includes(u.status) && (
                  <button onClick={() => setPwTarget(u)} data-testid={`emp-set-password-mobile-${u.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                    <Key size={10} className="inline" /> Password
                  </button>
                )}
                {["invited", "invitation_pending", "invitation_expired"].includes(u.status) && (
                  <button onClick={() => resend(u.id)} data-testid={`emp-resend-mobile-${u.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                    <Envelope size={10} className="inline" /> Resend
                  </button>
                )}
                {needsWelcome(u) && (
                  <ResendWelcomeButton user={u} onDone={load} testId={`emp-resend-welcome-mobile-${u.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-50" />
                )}
                {canFixEmail(u) && u.id !== user?.id && (
                  <FixEmailButton user={u} onDone={load} testId={`emp-fix-email-mobile-${u.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white" />
                )}
                {u.status === "active" && u.id !== user?.id && (
                  <button onClick={() => action(u.id, { status: "suspended" }, "Suspended")} data-testid={`emp-suspend-mobile-${u.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                    <PauseCircle size={10} className="inline" /> Suspend
                  </button>
                )}
                {["suspended", "deactivated", "locked"].includes(u.status) && (
                  <button onClick={() => action(u.id, { status: "active" }, "Reactivated")} data-testid={`emp-activate-mobile-${u.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white">
                    <ArrowClockwise size={10} className="inline" /> Reactivate
                  </button>
                )}
                {canSetPassword && u.id !== user?.id && u.status !== "archived" && (
                  <button onClick={() => setRemoveTarget(u)} data-testid={`emp-remove-mobile-${u.id}`}
                    className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                    <Archive size={10} className="inline" /> Remove
                  </button>
                )}
                {u.status === "archived" && (
                  <span className="text-[10px] text-zinc-500 break-all" data-testid={`emp-archived-note-mobile-${u.id}`}>
                    Was {u.archived_email}
                  </span>
                )}
              </div>
            </div>
          ))}
          {!rows.length && <div className="py-6 text-center label-mono">{onboarding ? "Everyone here has finished onboarding" : "No employees"}</div>}
        </div>
      </div>
      {assignTarget && (
        <AssignPagesDialog
          employee={assignTarget}
          onClose={() => setAssignTarget(null)}
          onSaved={load}
        />
      )}
      {removeTarget && (
        <RemoveEmployeeDialog employee={removeTarget} onClose={() => setRemoveTarget(null)} onDone={load} />
      )}
      {pwTarget && (
        <SetPasswordDialog
          user={pwTarget}
          onClose={() => setPwTarget(null)}
          onSaved={load}
          testIdPrefix="emp-set-pw"
        />
      )}
      {seedOpen && (
        <SeedTestAccountsDialog
          onClose={() => setSeedOpen(false)}
          onSaved={load}
        />
      )}
      {/* Iter-73: factory-reset dialog. Super_admin only, backend enforced. */}
      <FactoryResetDialog
        open={factoryOpen}
        onClose={() => setFactoryOpen(false)}
        onReset={() => {
          load();
          // Iter-73.1: kick the snapshots panel to pick up the fresh dump.
          setSnapshotsRefreshKey((k) => k + 1);
        }}
      />
      <CleanStartDialog
        open={cleanOpen}
        onClose={() => setCleanOpen(false)}
        onDone={() => { load(); setCleanKey((k) => k + 1); }}
      />
      {canSeedTests && (
        <CleanStartBackups refreshKey={cleanKey} onRestored={load} />
      )}
      {/* Iter-73.1: undo snapshots. Component self-hides when empty. */}
      {canSeedTests && (
        <FactoryResetSnapshotsPanel refreshKey={snapshotsRefreshKey} />
      )}
    </div>
  );
}
